import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { filter, Observable, take, takeWhile } from "rxjs";
import { AuthService } from "./auth/auth.service";
import { getUser } from "./auth/state/selectors";
import { WebsocketService } from "./conversation/websocket.service";
import { Notification } from "./models/Notification";
import { User } from "./models/User";
import { NotificationDto } from "./notifications/dto/notification.dto";
import { NotificationService } from "./notifications/notification.service";
import { getVisibleNotifications } from "./notifications/state/selectors";
import { Practice } from "./models/Practice";
import {
  getAvailablePractices,
  getCurrentPractice,
} from "./practices/state/selectors";
import { EnvironmentService } from "./services/environment.service";
import { AppState } from "./state/reducers";
import {
  getHealth,
  getWebsocketHealth,
  isConversationPreviewEnabled,
} from "./state/selectors";
import {
  DoHealthCheck,
  GetCurrencies,
  GetTags,
  SetConversationPreviewOpen,
  SetEmbedMode,
  SetEmbedSettings,
  SetPreviewConversationId,
  ToggleConversationPreviewEnabled,
} from "./state/actions";
import { SocketService } from "./services/socket.service";
import { NavigationEnd, Router, RouterEvent } from "@angular/router";
import { GoogleAnalyticsService } from "ngx-google-analytics";
import { Title } from "@angular/platform-browser";
import { AppService } from "./app.service";
import { SetPmsHealthy, UpdatePracticeFormSubmissionCount, UpdatePracticeName, UpdatePracticeTheme } from "./practices/state/actions";
import { GetFlowConfigurations } from "./automation/state/actions";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
  title = "messaging-front-end";
  alive = true;
  timeout?: any;
  healthCheckTimeout?: any;
  notifications$?: Observable<Notification[]>;
  practices$?: Observable<Practice[] | null>;
  user$?: Observable<User | null>;
  user: User | null = null;
  healthy$?: Observable<boolean>;
  websocketHealthy$?: Observable<boolean>;
  previewEnabled: boolean = false;
  socketConnected = false;
  practice?: Practice;
  embedMode = false;

  constructor(
    private authService: AuthService,
    private store: Store<AppState>,
    private environmentService: EnvironmentService,
    private websocketService: WebsocketService,
    private notificationService: NotificationService,
    private socketService: SocketService,
    private router: Router,
    private gaService: GoogleAnalyticsService,
    private titleService: Title,
    private appService: AppService,
    private translateService: TranslateService
  ) {
    this.router.events
      .pipe(
        filter((event: any): event is NavigationEnd => event instanceof NavigationEnd),
        take(1)
      )
      .subscribe((event: NavigationEnd) => {
        if (event.urlAfterRedirects.startsWith('/embed')) {
          this.setEmbedMode();
          this.setEmbedSettings(event);
        }

        const url = new URL(window.location.href);
        if (url.searchParams.has('jwt') || url.searchParams.has('access_token') || url.searchParams.has('refresh_token')) {
          const paramsToRemove = ['jwt', 'access_token', 'refresh_token'];

          const url = new URL(window.location.href);
          paramsToRemove.forEach(param => url.searchParams.delete(param));

          window.history.replaceState({}, '', url.toString());
        }

        this.initialise();
      });

    this.doHealthCheck();
    this.subscribeToHealthy();
    this.subscribeToWebsocketHealthy();
    this.websocketService.onConnect$.subscribe((connected) => {
      this.socketConnected = true;
      this.joinSocket();
    });

    this.websocketService.getPmsStatusUpdates().subscribe((pmsHealthy) => {
      this.store.dispatch(
        SetPmsHealthy({
          pmsHealthy: pmsHealthy,
        }),
      );
    });

    this.websocketService.getUpdatedPractice().subscribe((practice) => {
      this.store.dispatch(UpdatePracticeName({ id: practice.id, newName: practice.name }));
      this.store.dispatch(UpdatePracticeTheme({ id: practice.id, newTheme: practice.theme }));
    });

    this.websocketService.getPracticeCounters().subscribe((data) => {
      this.store.dispatch(UpdatePracticeFormSubmissionCount({ id: data.practiceId, formSubmissionCount: data.formSubmissionCount }));
    });
  }

  ngOnInit(): void {

  }

  ngAfterViewInit(): void {

  }

  ngOnDestroy(): void {
    this.alive = false;
    clearInterval(this.healthCheckTimeout);
  }

  initialise(): void {
    this.store.dispatch(SetEmbedMode({enabled: this.embedMode}));

    if (this.embedMode === false) {
      this.regularInitialisation();
    } else {
      this.embeddedInitialisation();
    }
  }

  regularInitialisation(): void {
    this.handleLanguageChange();
    this.subscribeToCurrentPractice();
    this.subscribeToUser();
    this.subscribeToPractices();
    this.initialiseOneSignal();

    this.authService.recoverSession();

    this.setDocHeight();

    this.setUpNotificationWebsocketConnections();
    this.subscribeToNotifications();

    this.store.dispatch(GetFlowConfigurations());

    this.store
      .pipe(select(isConversationPreviewEnabled))
      .pipe(takeWhile(() => this.alive))
      .subscribe((enabled) => (this.previewEnabled = enabled));

    const previewEnabled = window.localStorage.getItem("preview-pane");

    if (previewEnabled == "1") {
      this.store.dispatch(ToggleConversationPreviewEnabled());
    }

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (
          this.previewEnabled &&
          (event.url.includes("/day-list") ||
            event.url.includes("/conversations?"))
        ) {
          this.store.dispatch(SetConversationPreviewOpen({ open: true }));
        } else {
          this.store.dispatch(SetConversationPreviewOpen({ open: false }));
          this.store.dispatch(SetPreviewConversationId({ id: null }));
        }

        if (event.id > 1) {
          this.gaService.pageView(
            event.urlAfterRedirects,
            this.titleService.getTitle(),
          );
        }
      }
    });
  }

  embeddedInitialisation(): void {
    this.subscribeToCurrentPractice();
    this.subscribeToUser();
    this.subscribeToPractices();
  }

  private initialiseOneSignal(): void {
    this.appService.initialiseOneSignal();
  }

  @HostListener("document:visibilitychange", ["$event"])
  visibilityChange(): void {
    if (
      document.visibilityState !== "hidden" &&
      !this.socketService.ioSocket.connected
    ) {
      window.location.href = window.location.href;
    }
  }

  @HostListener("window:resize", ["$event"])
  onResize(): void {
    this.handleResize();
  }

  @HostListener("window:orientationchange", ["$event"])
  onOrientationChange(): void {
    this.setDocHeight();
  }

  setEmbedMode(): void {
    this.embedMode = true;
  }

  setEmbedSettings(event: NavigationEnd): void {
    const params = new URLSearchParams(event.urlAfterRedirects.split('?')[1]);
    const cloudId = params.get('cloudId');
    const departmentId = params.get('clinicId');

    if (cloudId && departmentId) {
      this.store.dispatch(SetEmbedSettings({cloudId: cloudId, departmentId: departmentId }));
    }
  }

  handleResize(): void {
    const el = document.activeElement;
    if (
      !el ||
      (el.tagName.toLowerCase() !== "input" &&
        el.tagName.toLowerCase() !== "textarea")
    ) {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => this.setDocHeight(), 100);
    }
  }

  setDocHeight(): void {
    document.documentElement.style.setProperty(
      "--vh",
      `${window.innerHeight / 100}px`,
    );
  }

  setUpNotificationWebsocketConnections(): void {
    this.store.pipe(select(getUser)).subscribe((user) => {
      if (user) {
        this.user = user;
        this.websocketService.joinNotificationsChannel(Number(user.id));
      }
    });

    this.websocketService.onConnect$.subscribe((connected) => {
      if (connected && this.user) {
        this.websocketService.joinNotificationsChannel(Number(this.user.id));
      }
    });

    this.websocketService
      .getNotifications()
      .subscribe(
        (payload: { notification: NotificationDto; showToast: boolean }) => {
          this.notificationService.handleNewNotification(
            payload.notification,
            payload.showToast,
          );
        },
      );
  }

  subscribeToNotifications(): void {
    this.notifications$ = this.store.pipe(select(getVisibleNotifications));
  }

  subscribeToUser(): void {
    this.user$ = this.store.pipe(select(getUser));
    this.user$.subscribe((user) => {
      if (user) {
        this.doGetCurrencies();
        this.doGetTags();
      }
    });
  }

  subscribeToPractices(): void {
    this.practices$ = this.store.pipe(select(getAvailablePractices));
  }

  subscribeToCurrentPractice(): void {
    this.store.pipe(select(getCurrentPractice)).subscribe((practice) => {
      if (
        this.practice &&
        practice?.id != this.practice.id &&
        this.socketConnected
      ) {
        this.websocketService.leavePractice(this.practice.id);
      }

      if (practice && this.practice?.id != practice.id) {
        this.practice = practice;
        this.joinSocket();
      }
    });
  }

  joinSocket(): void {
    if (this.practice && this.socketConnected) {
      this.websocketService.joinPractice(this.practice.id);
    }
  }

  subscribeToWebsocketHealthy(): void {
    this.websocketHealthy$ = this.store.pipe(select(getWebsocketHealth));
  }

  subscribeToHealthy(): void {
    this.healthy$ = this.store.pipe(select(getHealth));
  }

  doHealthCheck(): void {
    this.store.dispatch(DoHealthCheck());

    this.healthCheckTimeout = setInterval(() => {
      this.store.dispatch(DoHealthCheck());
    }, 30000);
  }

  doGetCurrencies(): void {
    this.store.dispatch(GetCurrencies());
  }

  doGetTags(): void {
    this.store.dispatch(GetTags());
  }

  private restoreLanguage() {
    const isoCode = localStorage.getItem('isoCode') as string;

    if (isoCode) {
      this.translateService.use(isoCode)
    } else {
      this.translateService.use(this.translateService.getDefaultLang())
    }
    this.setLanguage(isoCode)
  }

  private handleLanguageChange() {
    this.store.select(getUser)
    .pipe(takeWhile(() => this.alive))
    .subscribe(user => {
      if(user?.isoCode) {
        this.translateService.use(user.isoCode);
        localStorage.setItem('isoCode', user.isoCode);
        this.restoreLanguage();
      }
    });
  }

  setLanguage(lang: string) {
    document.documentElement.setAttribute('lang', lang); // Updates <html lang="xx">
  }
}
